import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, FormControl, InputLabel, Select, MenuItem,
  Alert, Box, alpha, useTheme, Chip
} from '@mui/material';
import { ContentCopy, ArrowForward, Info } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { sessionsApi } from '../../services/sessions/sessionsApi';
import { teachingLoadsApi, CopyResult } from '../../services/sessions/teachingLoadsApi';
import { Session } from '../../types/sessions';

interface Props {
  open: boolean;
  targetSessionId: string;
  targetSessionName: string;
  onClose: () => void;
  onCopied: (result: CopyResult) => void;
}

const CopyFromPreviousSessionDialog: React.FC<Props> = ({
  open, targetSessionId, targetSessionName, onClose, onCopied
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [sourceId, setSourceId] = useState<string>('');
  const [loadingList, setLoadingList] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSourceId('');
    let cancelled = false;
    setLoadingList(true);
    (async () => {
      try {
        const list = await sessionsApi.list();
        if (cancelled) return;
        // Source sessions: anything except the target itself, sorted newest first.
        const eligible = list
          .filter(s => s._id !== targetSessionId)
          .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
        setSessions(eligible);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not load sessions', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, targetSessionId, enqueueSnackbar]);

  const sourceSession = useMemo(
    () => sessions.find(s => s._id === sourceId) ?? null,
    [sessions, sourceId]
  );

  const submit = async () => {
    if (!sourceId) return;
    setSubmitting(true);
    try {
      const result = await teachingLoadsApi.copyFromPreviousSession({
        sourceSessionId: sourceId,
        targetSessionId
      });
      const total = result.classTeachersCopied + result.subjectAssignmentsCopied;
      enqueueSnackbar(
        total > 0
          ? `Copied ${result.classTeachersCopied} class teacher${result.classTeachersCopied === 1 ? '' : 's'} and ${result.subjectAssignmentsCopied} subject assignment${result.subjectAssignmentsCopied === 1 ? '' : 's'}.`
          : 'Nothing to copy — the target already had every slot filled or the source was empty.',
        { variant: total > 0 ? 'success' : 'info' }
      );
      onCopied(result);
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Copy failed', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <ContentCopy color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Copy from previous session</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Carry forward every active class-teacher and subject-teacher assignment from a prior session into{' '}
          <strong>{targetSessionName}</strong>. Already-filled slots are left untouched.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <FormControl fullWidth disabled={loadingList || submitting}>
            <InputLabel>Source session</InputLabel>
            <Select value={sourceId} label="Source session" onChange={(e) => setSourceId(e.target.value)}>
              {sessions.map(s => (
                <MenuItem key={s._id} value={s._id}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                    <Typography variant="body2">{s.name}</Typography>
                    {s.status === 'completed' && (
                      <Chip size="small" label="Completed" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
                    )}
                    {s.status === 'archived' && (
                      <Chip size="small" label="Archived" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
                    )}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {sourceSession && (
            <Box sx={{
              p: 1.5, borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.04)
            }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="body2" fontWeight={600}>{sourceSession.name}</Typography>
                <ArrowForward fontSize="small" sx={{ color: 'text.disabled' }} />
                <Typography variant="body2" fontWeight={600}>{targetSessionName}</Typography>
              </Stack>
            </Box>
          )}

          <Alert
            severity="info"
            icon={<Info fontSize="inherit" />}
            sx={{ '& .MuiAlert-message': { width: '100%' } }}
            variant="outlined"
          >
            <Stack spacing={0.5}>
              <Typography variant="body2">
                Sections are matched by <strong>name within the same class</strong> across sessions.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Inactive teachers are skipped. Slot mappings without a name match show up as "unassigned" — pick them up manually after the copy.
              </Typography>
            </Stack>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          onClick={submit}
          disabled={!sourceId || submitting}
          startIcon={<ContentCopy fontSize="small" />}
          sx={{ borderRadius: 2 }}
        >
          {submitting ? 'Copying…' : 'Copy assignments'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyFromPreviousSessionDialog;
